import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  ConsolidatedContact,
  RecommendedContactStatusOptionsObject,
  ContactStatusOptionsObject,
} from '../../api/CoachingNotes/schema';
import { GET_CONSOLIDATED_CONTACTS } from '../../api/FormLibrary/queries';
import { getWeekAndYear } from '../../Util';
import { createConsolidatedContact } from '../../api/Utils/helper';
import Storage from '../../Util/Storage';
import { Profile } from '../../Types';
import { ContactStatus } from '../../PerformancePortal/Dashboard/DashboardComponents/HmdDetails/types';
import { INGRESS_SOURCES } from '../../Util/Form/formData.constants';

export interface UseContactListOptions {
  userID: string;
  userName: string;
  startWeek: string;
  endWeek: string;
  skip?: boolean;
  // Optional parameters for realtime HMD contact injection
  contactId?: string;
  ingress?: string | null;
  profile?: Profile;
  cscEntityPrefix?: string;
  contactStatusDetails?: ContactStatus;
}

export const useContactList = ({
  userID,
  userName,
  startWeek,
  endWeek,
  skip = false,
  contactId,
  ingress,
  profile,
  cscEntityPrefix,
  contactStatusDetails,
}: UseContactListOptions) => {
  // Parse week strings (YYYY-WW format) into year and week integers
  const { year: startYear, week: startWeekNum } = getWeekAndYear(startWeek);
  const { year: endYear, week: endWeekNum } = getWeekAndYear(endWeek);

  const { data, loading, error, refetch } = useQuery(
    GET_CONSOLIDATED_CONTACTS,
    {
      variables: {
        userID,
        userName,
        startWeek: startWeekNum,
        startYear,
        endWeek: endWeekNum,
        endYear,
      },
      skip,
      fetchPolicy: 'cache-and-network', // Always fetch fresh data
    }
  );

  const baseContacts = data?.getConsolidatedContacts ?? [];

  // Process contacts with realtime HMD injection (immutable)
  const contacts = useMemo(() => {
    // Start with a copy of base contacts
    let processedContacts = [...baseContacts];

    // Handle realtime HMD contact injection
    // If we have a contactId from realtime HMD that's not in the fetched list,
    // retrieve it from localStorage and inject it
    if (
      ingress === INGRESS_SOURCES.REALTIME_HMD &&
      contactId &&
      !loading &&
      baseContacts.length > 0 &&
      profile &&
      cscEntityPrefix
    ) {
      const contactExists = baseContacts.some(c => c.contactID === contactId);
      if (!contactExists) {
        const storedContact = Storage.getItem('realTimeHMDContactForReview');
        if (storedContact) {
          try {
            const parsedContact = JSON.parse(storedContact);
            const consolidatedContact = createConsolidatedContact(
              parsedContact,
              cscEntityPrefix,
              profile.user.userID,
              contactId
            );

            const contactStatus = contactStatusDetails?.contactStatuses?.find(
              (status: any) => status.commId === contactId
            );

            const mergedContact: ConsolidatedContact = {
              ...consolidatedContact,
              contactStatus:
                contactStatus?.contactStatus ||
                ContactStatusOptionsObject.NOTREVIEWED,
              formReviews: contactStatus?.formReviews || [],
            };

            processedContacts = [mergedContact, ...baseContacts];
          } catch (error) {
            console.error('Error injecting realtime HMD contact:', error);
          }
        }
      }
    }

    return processedContacts;
  }, [
    baseContacts,
    ingress,
    contactId,
    loading,
    profile,
    cscEntityPrefix,
    contactStatusDetails,
  ]);

  const getContactById = (
    contactId: string
  ): ConsolidatedContact | undefined => {
    return contacts.find(c => c.contactID === contactId);
  };

  const notReviewedContacts = contacts.filter(
    c => c.status === RecommendedContactStatusOptionsObject.NOTREVIEWED
  );

  return {
    contacts,
    loading,
    error,
    refetch,
    getContactById,
    notReviewedContacts,
  };
};
